import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import * as styles from './Input.scss';

/**
 * @class Input
 * @classdesc Reusable Input component.
 * @note Input value is uncontrolled
 * @property {Object} state
 * @example ./Input.md
 */
export default class Input extends Component {
  static propTypes = {
    autofocus: PropTypes.bool,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    divClass: PropTypes.string,
    type: PropTypes.string,
    error: PropTypes.string,
    errorClassName: PropTypes.string,
    hasError: PropTypes.bool,
    id: PropTypes.string,
    label: PropTypes.string,
    maxLength: PropTypes.number,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onClick: PropTypes.func,
    onKeyPress: PropTypes.func,
    onKeyDown: PropTypes.func,
    onKeyUp: PropTypes.func,
    placeholder: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    readOnly: PropTypes.bool,
    showCount: PropTypes.bool,
    showMaxLengthLabel: PropTypes.bool,
    size: PropTypes.string,
    style: PropTypes.objectOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
    ),
    subLabel: PropTypes.string,
    helpText: PropTypes.string,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    required: PropTypes.bool,
  };

  static defaultProps = {
    autofocus: false,
    className: null,
    disabled: false,
    divClass: null,
    error: null,
    errorClassName: null,
    hasError: false,
    id: null,
    label: null,
    maxLength: 0,
    onFocus: () => {},
    onBlur: () => {},
    onChange: () => {},
    onClick: () => {},
    onKeyPress: () => {},
    onKeyDown: () => {},
    onKeyUp: () => {},
    placeholder: '',
    readOnly: false,
    showCount: false,
    showMaxLengthLabel: false,
    size: 'SMALL',
    style: null,
    subLabel: '',
    helpText: '',
    value: '',
    type: 'text',
    required: false,
  };

  constructor(props) {
    super(props);
    const { value } = this.props;

    this.state = {
      count: 0,
      overLimit: false,
      value,
    };
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.value !== prevState.value) return { value: nextProps.value };
    return null;
  }

  /**
   * Calls onChange & updates stateful values
   * @param {Object} - event object
   * @return {Function} onChange
   */
  changeHandler = e => {
    const { maxLength, onChange } = this.props;

    this.setState({
      count: e.target.value.length,
      overLimit: e.target.value.length > maxLength,
    });
    return onChange(e);
  };

  render() {
    const {
      autofocus,
      className,
      disabled,
      divClass,
      error,
      errorClassName,
      hasError,
      id,
      label,
      maxLength,
      onFocus,
      onBlur,
      onClick,
      onKeyUp,
      onKeyDown,
      onKeyPress,
      placeholder,
      readOnly,
      showCount,
      showMaxLengthLabel,
      size,
      style,
      subLabel,
      helpText,
      required,
      type,
    } = this.props;
    const { count, overLimit, value } = this.state;

    return (
      <div className={classNames(styles.inputContainer, divClass)}>
        <div className={styles.labelSection}>
          {label &&
            <p className={classNames(disabled ? styles.labelDisabled : styles.inputLabel, required ? styles.required : null)}>
              {id ?
                <label htmlFor={id}>{label}</label> :
                <>{label}</>
              }
            </p>}
          {(maxLength > 0 && showMaxLengthLabel) &&
            <span className={styles.maxLength}>{`Max Length: ${maxLength}`}</span>}
          {(subLabel) &&
            <span className={styles.maxLength}>{subLabel}</span>}
        </div>
        {(helpText) &&
          <div className={styles.break}>
            <div className={styles.labelSection}>
              <span className={styles.helpText}>{helpText}</span>
            </div>
          </div>}
        <input
          className={classNames(
            styles.input,
            { [styles.error]: hasError || (maxLength !== 0 && overLimit) },
            { [styles.disabled]: disabled },
            { [styles.inputLarge]: size === 'LARGE' },
            { [styles.inputHuge]: size === 'HUGE' },
            className,
          )}
          type={type}
          autoFocus={autofocus}
          disabled={disabled}
          id={id}
          maxLength={maxLength === 0 ? null : maxLength}
          onFocus={onFocus}
          onChange={this.changeHandler}
          onClick={onClick}
          onBlur={onBlur}
          onKeyUp={onKeyUp}
          onKeyDown={onKeyDown}
          onKeyPress={onKeyPress}
          placeholder={placeholder}
          readOnly={readOnly}
          style={style}
          value={value} />
        {!disabled && hasError && error &&
          <p className={classNames(styles.errorText, errorClassName)}>
            {error}
          </p>}
        {maxLength > 0 && showCount && (maxLength - count <= 30) &&
          <p className={classNames(styles.labelCount, { [styles.errorCount]: (maxLength !== 0 && overLimit) })}>
            {`${count}/${maxLength}`}
          </p>}
      </div>
    );
  }
}
